import Button from '@mui/material/Button';
import { ReactComponent as EyeIcon } from 'assets/icons/eyeIcon.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/savePencilIcon.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/closeIcon.svg';
import {
    ButtonsWrapper,
    CloseIconWrapper,
    FileRow,
    FilenameWrapper,
    FilesWrapper,
    RemovedPagesText,
    SubmitButtonsWrapper,
    Wrapper
} from './SaveModal.style';
import { ModalType } from '../../../components/Dialogs/types';
import { useAppSelector } from '../../../store/hooks';
import { selectNewFiles } from '../../../store/features/newFiles/selectors';
import { selectPages } from '../../../store/features/pages/selectors';
import Tooltip from '@mui/material/Tooltip';
import { polishPlurals } from 'polish-plurals';
import { requestUploadNewFiles } from '../../../api/api';
import { Environment, FileInfo, SourcePageDetail } from '../../../api/api.type';
import { createdFiles as createdFilesBLOBs } from '../../../store/features/newFiles/actions';
import { selectTotalUploads } from '../../../store/features/progress/uploadSelectors';
import { removeFileExtension } from '../../../helpers';
import ProgressSpinner from '../../../components/ProgressSpinner/ProgressSpinner';
import { FileDesc } from '../../../store/features/newFiles/newFilesSlice';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { selectEnvironment, selectSessionId } from '../../../store/features/context/selectors';
import { EntityId } from '@reduxjs/toolkit';
import { Typography } from '@mui/material';
import React from 'react';

function formatFileSize(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const MAX_SIZE = 25 * 1024 * 1024;

type SaveModalProps = {
    type: ModalType;
    actualPageId: EntityId;
    toggleModalOpen: (type: ModalType, file?: string | number) => void;
    showRemoved?: boolean;
    toggleShowRemoved?: () => void;
};

const FileRowComponent = ({
    filename,
    file,
    toggleModalOpen
}: {
    filename?: string;
    file: FileDesc;
    toggleModalOpen: (type: ModalType, file?: string | number) => void;
}) => {
    return (
        <FileRow key={file.blobURL} className='fileRow'>
            <Tooltip sx={{ width: 'calc(100% - 74px)' }} title={filename} PopperProps={{ disablePortal: true }}>
                <Box>
                    <Typography variant='body2' sx={{ lineHeight: '20px' }} noWrap component='div'>
                        {filename}
                    </Typography>
                    <Typography variant='caption' sx={{ lineHeight: '16px' }} noWrap component='div'>
                        {formatFileSize(file.size)}
                    </Typography>
                    {file.size >= MAX_SIZE ? (
                        <Typography variant='caption' sx={{ lineHeight: '16px', color: '#D32F2F' }}>
                            Zbyt duży rozmiar pliku. Maksymalny rozmiar to 25 MB.
                        </Typography>
                    ) : null}
                </Box>
            </Tooltip>
            <ButtonsWrapper>
                <EyeIcon
                    id={`save-modal-file-preview-buttton-${file.filename}`}
                    onClick={() => toggleModalOpen('pdfPreview', file.blobURL)}
                />
                <PencilIcon
                    id={`save-modal-file-rename-buttton-${file.filename}`}
                    onClick={() => toggleModalOpen('renameFileFromSave', file.pageId)}
                />
            </ButtonsWrapper>
        </FileRow>
    );
};

const SaveModal = ({ toggleModalOpen, showRemoved: showRemovedPages, toggleShowRemoved }: SaveModalProps) => {
    const newFiles = useAppSelector(selectNewFiles);
    const isLoading = useAppSelector(state => state.newFiles.loading);
    const sessionId = useAppSelector(selectSessionId);
    const pages = useAppSelector(selectPages);

    const uploadFiles = async () => {
        requestUploadNewFiles({
            sessionId,
            files: newFiles.map<FileInfo>(newFileDesc => ({
                file: createdFilesBLOBs[newFileDesc.blobURL],
                filename: newFileDesc.filename,
                id: newFileDesc.id,
                deleted: newFileDesc.removed,
                sourcePagesDetails: newFileDesc.details.map<SourcePageDetail>(
                    ({ sourceFileRefId, sourceFilePageNr }) => ({
                        sourceFileRefId,
                        sourcePageNr: sourceFilePageNr - 1
                    })
                )
            }))
        });
    };

    const { createdFiles, hiddenFiles } = useMemo(
        () => ({
            hiddenFiles: newFiles.filter(p => p.removed),
            createdFiles: newFiles.filter(p => !p.removed)
        }),
        [newFiles]
    );

    const environment = useAppSelector(selectEnvironment);

    const descriptionString1 = `${polishPlurals('zostanie utworzony', 'zostaną utworzone', 'zostanie utworzonych', createdFiles.length)}`;
    const descriptionString2 = `${polishPlurals('nowy plik:', 'nowe pliki:', 'nowych plików:', createdFiles.length)}`;
    const saveStringPortalBiura = `${polishPlurals('Prześlij plik', 'Prześlij pliki', 'Prześlij pliki', createdFiles.length)}`;
    const saveStringNexo = `${polishPlurals('Zapisz plik', 'Zapisz pliki', 'Zapisz pliki', createdFiles.length)}`;

    const inProgress = useAppSelector(selectTotalUploads) > 0;
    const fileToBig = useMemo(() => createdFiles.some(p => p.size >= MAX_SIZE), [createdFiles]);
    return (
        <Wrapper>
            <h1>Zapisz zmiany</h1>
            {isLoading ? (
                <Box
                    sx={{
                        width: '100%',
                        height: '150px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Typography
                        variant='body2'
                        sx={{ margin: '16px 0' }}
                        style={fileToBig ? { color: '#D32F2F' } : undefined}
                    >
                        {fileToBig
                            ? 'Nie można zapisać plików. Niektóre pliki przekraczają maksymalny dopuszczalny rozmiar (25 MB).'
                            : `Po zapisie ${descriptionString1} ${createdFiles.length} ${descriptionString2}`}
                    </Typography>
                    <Box overflow='hidden' position='relative'>
                        <FilesWrapper className='filesWrapper'>
                            {createdFiles.map(file => {
                                const filename = removeFileExtension(
                                    pages.entities[file.pageId]?.userFileName ??
                                        pages.entities[file.pageId]?.fileName.new ??
                                        pages.entities[file.pageId]?.fileName.old
                                );

                                return (
                                    <FileRowComponent
                                        key={file.id}
                                        file={file}
                                        toggleModalOpen={toggleModalOpen}
                                        filename={filename}
                                    />
                                );
                            })}
                        </FilesWrapper>
                    </Box>
                    {hiddenFiles.length > 0 ? (
                        <RemovedPagesText onClick={toggleShowRemoved}>
                            {showRemovedPages ? 'Ukryj usunięte strony' : 'Pokaż usunięte strony'}
                        </RemovedPagesText>
                    ) : null}
                    {showRemovedPages ? (
                        <FilesWrapper>
                            {hiddenFiles.map(fileDesc => {
                                return (
                                    <FileRow removed key={fileDesc.id}>
                                        <FilenameWrapper>{fileDesc.filename}</FilenameWrapper>
                                        <ButtonsWrapper style={{ width: '40px' }}>
                                            <EyeIcon
                                                id={`save-modal-removed-page-preview-buttton-${fileDesc.filename}`}
                                                onClick={() => toggleModalOpen('pagePreviewFromSave', fileDesc.pageId)}
                                            />
                                        </ButtonsWrapper>
                                    </FileRow>
                                );
                            })}
                        </FilesWrapper>
                    ) : null}
                    <SubmitButtonsWrapper>
                        <Button variant='text' onClick={() => toggleModalOpen(null)}>
                            Wróć
                        </Button>
                        <Button variant='contained' onClick={uploadFiles} disabled={inProgress || fileToBig}>
                            {environment === Environment.WEB
                                ? `${saveStringPortalBiura} (${createdFiles.length})`
                                : `${saveStringNexo} (${createdFiles.length})`}
                        </Button>
                    </SubmitButtonsWrapper>
                    {inProgress && <ProgressSpinner />}
                </>
            )}
            <CloseIconWrapper>
                <CloseIcon onClick={() => toggleModalOpen(null)} />
            </CloseIconWrapper>
        </Wrapper>
    );
};

export default SaveModal;
